import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MY_RFQ_DRAFTS_KEY, removeItemFromCache, useCreateRFQ } from '@steelbuy/data-access';
import {
    MaterialWithDimensions,
    getRFQFields,
    RFQField,
    rfqHeadings,
    RFQDetailsHeading,
    QuoteEntryDetail,
    RFQQuoteEntryMaterial,
} from '@steelbuy/ts-shared';
import { MarketingBannerSteelbuyPromise } from '@steelbuy/ui-domain';
import {
    ButtonCallToAction,
    ButtonTertiaryOnLightM,
    LoadingSpinnerOverlay,
    Notification,
    NotificationLevel,
    WizardBar,
    WizardBarItemStatus,
} from '@steelbuy/ui-primitive';
import { isRequestQuotePath } from '@steelbuy/util';
import { RoutePath } from '../../../router/Routes';
import { TableLayout } from '../../../views/layouts/table-layout/TableLayout';
import { CreateRFQUnsavedChangesModal } from '../../create-rfq-unsaved-changes/CreateRFQUnsavedChangesModal';
import LegalCheckboxes from '../../legal-checkboxes/LegalCheckboxes';
import { PageHeader } from '../../page-header/PageHeader';
import { CardContentRFQDetails } from '../../rfqs/card-content-rfq-details/CardContentRFQDetails';
import { CardContentAdditionalComments } from '../../rfqs/CardContentAdditionalComments';
import { CardContentDeliveryMonth } from '../../rfqs/CardContentDeliveryMonth';
import { CardContentOrigins } from '../../rfqs/CardContentOrigins';
import { CardContentQuoteDeadline } from '../../rfqs/CardContentQuoteDeadline';
import {
    getCreateRequestQuoteModel,
    getCreateRFQModel,
    getRFQSteps,
    getStepRoute,
    hasFormData,
} from '../createRFQUtil';
import { useRfqFormContext } from '../RfqFormContext';
import {
    Step3Type,
    isStep1WithDefinition,
    isStep1WithPlateType,
    isStep2,
    isStep3,
    isStep3RequestQuote,
} from '../Schema';
import './CreateRFQSummary.scss';

const CURRENT_STEP_INDEX = 2;

const getRFQHeadings = (fields: RFQField[], materialsArray: MaterialWithDimensions[]) =>
    fields.reduce((acc, curr) => {
        const isThicknessRangeField = curr.field === 'thicknessMin' || curr.field === 'thicknessMax';
        const field = isThicknessRangeField ? 'thickness' : (curr.field as keyof RFQDetailsHeading);
        return field in rfqHeadings &&
            (rfqHeadings[field] !== undefined || field === 'thickness') &&
            (field === 'thickness' || materialsArray.some((materials) => materials[curr.field] !== undefined))
            ? {
                  ...acc,
                  [field]: { ...rfqHeadings[field] },
              }
            : acc;
    }, {} as RFQDetailsHeading);

export const CreateRFQSummary = () => {
    const { formData, draftId } = useRfqFormContext();
    const { t } = useTranslation(['translation', 'uiDomain']);
    const navigate = useNavigate();
    const [validity, setValidity] = useState(false);
    const [createError, setCreateError] = useState('');
    const createRFQMutation = useCreateRFQ();
    const queryClient = useQueryClient();
    const { pathname } = useLocation();
    const isRequestQuote = isRequestQuotePath(pathname);

    useEffect(() => {
        if (!hasFormData(formData)) {
            navigate(getStepRoute(1, isRequestQuote, draftId));
        }
    }, []);

    if ((!isRequestQuote && !isStep3(formData)) || !formData) {
        return null;
    }

    const handleSubmit = () => {
        if (!formData) {
            setCreateError(t('application.createRFQSummary.createRFQFailed'));
            return;
        }
        let payload = isRequestQuote ? getCreateRequestQuoteModel(formData) : getCreateRFQModel(formData);
        if (!payload) {
            setCreateError(t('application.createRFQSummary.createRFQFailed'));
            return;
        }
        if (draftId) {
            payload = { ...payload, draftId };
        }
        setCreateError('');
        createRFQMutation.mutate(payload, {
            onSuccess: ({ quoteReferenceNumber, id }) => {
                if (draftId) {
                    removeItemFromCache(queryClient, [MY_RFQ_DRAFTS_KEY], draftId);
                }
                navigate(isRequestQuote ? RoutePath.CREATE_REQUEST_QUOTE_SUCCESS : RoutePath.CREATE_RFQ_SUCCESS, {
                    state: { quoteReferenceNumber, quoteId: id },
                });
            },
            onError: () => setCreateError(t('application.createRFQSummary.createRFQFailed')),
        });
    };

    const step3Data = isStep3(formData) || isStep3RequestQuote(formData) ? formData : undefined;
    const { origin, delivery, deadline, comments } = step3Data ?? ({} as Step3Type);
    let quoteEntries: QuoteEntryDetail[] = [];
    if (isStep1WithPlateType(formData) && isStep2(formData)) {
        quoteEntries = formData.plateType.map((plateType) => ({
            materialType: formData.materialType,
            product: formData.product,
            group: {
                label: 'plateType',
                value: plateType,
            },
            fieldHeadings: getRFQHeadings(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getRFQFields({ ...formData, plateType }, t as any),
                formData.materials[plateType]
            ),
            materials: formData.materials[plateType] as RFQQuoteEntryMaterial[],
        }));
    } else if (isStep1WithDefinition(formData) && isStep2(formData)) {
        quoteEntries = formData.definition.map((definition) => ({
            materialType: formData.materialType,
            product: formData.product,
            group: {
                label: 'definition',
                value: definition,
            },
            fieldHeadings: getRFQHeadings(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getRFQFields({ ...formData, definition }, t as any),
                formData.materials[definition]
            ),
            materials: formData.materials[definition] as RFQQuoteEntryMaterial[],
        }));
    }

    return (
        <div className="create-rfq-summary">
            <PageHeader pageTitle={t('application.createRFQSummary.header')} />
            {createRFQMutation.isLoading && <LoadingSpinnerOverlay />}
            <div className="create-rfq-summary__wizard">
                <TableLayout>
                    <WizardBar
                        items={getRFQSteps(isRequestQuote).map((step, index) => ({
                            label: t(`application.createRFQSteps.labels.${step}`),
                            status: WizardBarItemStatus.SUCCESS,
                            selected: false,
                            onClick: () => {
                                navigate(getStepRoute(index + 1, isRequestQuote, draftId), { replace: true });
                            },
                        }))}
                        currentStepIndex={CURRENT_STEP_INDEX}
                    />
                </TableLayout>
            </div>
            <div className="create-rfq-summary__layout">
                {createError && (
                    <Notification
                        level={NotificationLevel.ERROR}
                        message={createError}
                        onClose={() => setCreateError('')}
                        closeButton
                        stayOpen
                    />
                )}
                <CardContentRFQDetails quoteEntries={quoteEntries} />
                {origin && <CardContentOrigins origin={origin} />}
                {delivery && <CardContentDeliveryMonth delivery={delivery} />}
                {deadline && <CardContentQuoteDeadline deadline={deadline} />}
                {comments && (
                    <div className="create-rfq-summary__comments">
                        <CardContentAdditionalComments comments={comments} />
                    </div>
                )}
                <MarketingBannerSteelbuyPromise />

                <LegalCheckboxes setValidity={setValidity} />
                <div className="create-rfq-summary__layout__actionbar">
                    <ButtonTertiaryOnLightM
                        label={t('uiDomain:common.edit')}
                        onClick={() => navigate(getStepRoute(1, isRequestQuote, draftId))}
                    />
                    <ButtonCallToAction
                        disabled={!validity}
                        label={t('application.createRFQ.submitButtonLabel')}
                        onClick={handleSubmit}
                    />
                </div>
            </div>
            {!isRequestQuote && (
                <CreateRFQUnsavedChangesModal
                    onError={() => setCreateError(t('application.createDraftRFQ.createDraftRFQFailed'))}
                />
            )}
        </div>
    );
};
